/**
 * algolia-cart-fix.js
 * Intercepts Algolia InstantSearch's Add to Cart buttons on the search results page:
 *  - Prevents the /cart/add form redirect by blocking Algolia's form submission
 *  - POSTs to /cart/add.js via AJAX with sections=cart-drawer (one roundtrip)
 *  - Swaps cart-drawer HTML and opens/refreshes the drawer via theme events
 */
(function () {
  'use strict';

  // Skip the cart page itself
  if (window.location.pathname === ((window.Shopify?.routes?.cart) || '/cart')) return;

  const SECTIONS = ['cart-drawer'];

  function updateCount(n) {
    document.querySelectorAll('[data-cart-count]').forEach(el => el.setAttribute('data-cart-count', n));
    document.querySelector('.layout--viewport')?.setAttribute('data-cart-empty', String(n === 0));
  }

  function openDrawer() {
    window.dispatchEvent(new Event('theme:transition:reset:cart-drawer'));
    window.dispatchEvent(new CustomEvent('theme:drawer:open', {
      detail: { side: 'right', view: 'cart-drawer', trigger: null }
    }));
  }

  function applyCartHtml(html) {
    // Extract item count from rendered section HTML and update badge immediately
    try {
      const tmp = new DOMParser().parseFromString(html, 'text/html');
      const n = parseInt(tmp.querySelector('[data-cart-count]')?.textContent, 10);
      if (!isNaN(n)) updateCount(n);
    } catch (_) {}

    window.addEventListener('theme:cart:updated', openDrawer, { once: true });
    window.dispatchEvent(new CustomEvent('theme:cart:drawer:updateHtml', {
      detail: { section: html, load_transitions: false }
    }));
  }

  async function handleAtcClick(e) {
    const btn = e.target.closest('.ais-hit--cart-button');
    if (!btn || btn.classList.contains('ais-hit--cart-button__disabled')) return;

    // Block the form redirect but allow other handlers (e.g. Algolia Insights) to still fire
    e.preventDefault();

    // Resolve variant ID from data-form-id="algolia-add-to-cart-{variantId}"
    const formId = btn.getAttribute('data-form-id');
    let variantId = null;

    if (formId) {
      // Primary: numeric suffix of the form ID is the Shopify variant ID
      const m = formId.match(/(\d+)$/);
      if (m) variantId = m[1];
    }

    // Fallback: read hidden <input name="id"> inside the referenced form
    if (!variantId && formId) {
      const form = document.getElementById(formId);
      const idInput = form && form.querySelector('input[name="id"]');
      if (idInput) variantId = idInput.value;
    }

    if (!variantId) return;

    // Block if the product is restricted via the availability.Availability metafield.
    // _availabilityDisabledHandles is populated at runtime by patchOutOfStockButtons()
    // via the Section Rendering API (sections/algolia-product-availability.liquid).
    const hit = btn.closest('.ais-hit');
    if (hit && window._availabilityDisabledHandles) {
      const titleLink = hit.querySelector('.ais-hit--title a, a[href*="/products/"]');
      const hrefMatch = (titleLink?.getAttribute('href') || '').match(/\/products\/([^?#/]+)/);
      if (hrefMatch && window._availabilityDisabledHandles.has(hrefMatch[1])) {
        btn.textContent = 'NOT AVAILABLE';
        btn.classList.add('ais-hit--cart-button__disabled');
        btn.setAttribute('disabled', 'true');
        btn.setAttribute('aria-disabled', 'true');
        btn.style.setProperty('pointer-events', 'none', 'important');
        return;
      }
    }

    // Loading state
    btn.setAttribute('aria-busy', 'true');
    btn.classList.add('ais-hit--cart-button__loading');
    const originalText = btn.textContent;
    btn.textContent = 'Adding\u2026';

    let soldOut = false;

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: Number(variantId), quantity: 1, sections: SECTIONS })
      });

      if (!res.ok) {
        // 422 from Shopify cart API typically means the item is sold out / unavailable
        if (res.status === 422) {
          soldOut = true;
        }
        throw new Error('add failed');
      }

      const data = await res.json();

      if (typeof data.item_count === 'number') updateCount(data.item_count);

      const drawerHtml = data.sections?.['cart-drawer'];
      if (drawerHtml) {
        applyCartHtml(drawerHtml);
      } else {
        openDrawer();
      }
    } catch (_) {
      if (!soldOut) {
        // Non-inventory error — open drawer so user sees current cart state
        openDrawer();
      }
    } finally {
      btn.removeAttribute('aria-busy');
      btn.classList.remove('ais-hit--cart-button__loading');

      if (soldOut) {
        // Mark button as out of stock so the stale Algolia index result is corrected visually
        btn.textContent = 'OUT OF STOCK';
        btn.classList.add('ais-hit--cart-button__disabled');
        btn.setAttribute('disabled', 'true');
        btn.setAttribute('aria-disabled', 'true');
      } else {
        btn.textContent = originalText;
      }
    }
  }

  // Intercept button clicks in capture phase — runs before Algolia's delegated handler
  document.addEventListener('click', handleAtcClick, true);

  // Belt-and-suspenders: also block native form submissions for Algolia ATC forms
  document.addEventListener('submit', e => {
    const form = e.target;
    if (form && typeof form.id === 'string' && form.id.startsWith('algolia-add-to-cart-')) {
      // Prevent the page redirect; do not stopImmediatePropagation so Algolia tracking can still fire
      e.preventDefault();
    }
  }, true);

  // Block Navigation API redirects (Chrome/Edge 102+, Safari 18+) triggered by Algolia
  if (window.navigation) {
    window.navigation.addEventListener('navigate', e => {
      if (!e.destination) return;
      try {
        const dest = new URL(e.destination.url);
        if (dest.pathname !== location.pathname) {
          // Only block if we're mid-flight on an Algolia ATC (form was recently intercepted)
          // We check for any .ais-hit--cart-button[aria-busy] as a signal
          const busy = document.querySelector('.ais-hit--cart-button[aria-busy]');
          if (busy) e.preventDefault();
        }
      } catch (_) {}
    });
  }

})();
